/**
 * C0 Mate identity contract — persistent Crew Member face + name (Run 6)
 *
 * Persistence (no new product table):
 * 1. TEMPLATE bind → spaces.display_preferences.crew_member_template_bind
 *    `{ mateId, displayName }` — survives session restart (Sales Agent = fox)
 * 2. Meet-your-Crew draft → pulse prefs meta.crew_member_draft
 *    `{ mateId, displayName, bindStatus: NOT_BOUND_YET }` — survives but unbound
 * 3. Template registry / catalog defaults — code-defined fallback only
 *
 * Honesty:
 * - TEMPLATE identity ≠ Helper row bind (still NOT_BOUND_YET)
 * - Do not invent helperId from this module
 *
 * Preserve LosiAnimated. No Muse/Vesper.
 */

import {
  DEFAULT_MATE_ID,
  MATE_BY_ID,
  resolveMateId,
  type Mate,
  type MateId
} from '@/lib/crew/mates'
import {
  CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET,
  extractCrewMemberDraft,
  normalizeCrewMemberDisplayName,
  type CrewMemberDraft
} from '@/lib/crew/member-bind'
import {
  extractCrewMemberTemplateBinding,
  type CrewMemberTemplateBinding
} from '@/lib/crew/member-template-bind'
import {
  getCrewMemberTemplate,
  type CrewMemberTemplate,
  type CrewMemberTemplateId
} from '@/lib/crew/member-templates'

/** Persistent Member face + name — C0 contract shape. */
export type CrewMemberIdentity = {
  mateId: MateId
  displayName: string
}

export type CrewMemberIdentitySource =
  | 'template_bind'
  | 'member_draft'
  | 'template_registry'
  | 'catalog_default'

export type ResolvedCrewMemberIdentity = CrewMemberIdentity & {
  source: CrewMemberIdentitySource
  /** True when backed by Space TEMPLATE bind or saved Meet-your-Crew draft. */
  persistent: boolean
  templateId?: CrewMemberTemplateId
  bindStatus?: typeof CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET | 'TEMPLATE_BOUND'
}

export const CREW_MEMBER_IDENTITY_DISPLAY_NAME_MAX = 40

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

/** Normalize raw `{ mateId, displayName }` — invalid mate → null. */
export function normalizeCrewMemberIdentity(
  raw: unknown
): CrewMemberIdentity | null {
  if (!isRecord(raw)) return null
  const mateId = resolveMateId(
    typeof raw.mateId === 'string'
      ? raw.mateId
      : typeof raw.mate_id === 'string'
        ? raw.mate_id
        : null
  )
  if (!mateId || !MATE_BY_ID.has(mateId)) return null
  const displayName = normalizeCrewMemberDisplayName(
    raw.displayName ?? raw.display_name
  )
  return {
    mateId,
    displayName: displayName || MATE_BY_ID.get(mateId)?.label || mateId
  }
}

export function crewMemberIdentityFromTemplate(
  template: CrewMemberTemplate,
  overrides?: { displayName?: string }
): CrewMemberIdentity {
  return {
    mateId: template.mateId,
    displayName:
      overrides?.displayName?.trim().slice(0, CREW_MEMBER_IDENTITY_DISPLAY_NAME_MAX) ||
      template.displayName
  }
}

export function crewMemberIdentityFromTemplateBinding(
  binding: CrewMemberTemplateBinding
): CrewMemberIdentity {
  return {
    mateId: binding.mateId,
    displayName: binding.displayName
  }
}

export function crewMemberIdentityFromDraft(
  draft: CrewMemberDraft
): CrewMemberIdentity {
  return {
    mateId: draft.mateId,
    displayName:
      draft.displayName.trim() ||
      MATE_BY_ID.get(draft.mateId)?.label ||
      draft.mateId
  }
}

/**
 * Resolve Member identity with persistence priority:
 * template Space bind → Meet-your-Crew draft → template registry → catalog default.
 */
export function resolveCrewMemberIdentity(input: {
  displayPreferences?: unknown
  cardTypePreferences?: unknown
  templateId?: string | null
  fallbackMateId?: MateId | null
  fallbackDisplayName?: string | null
}): ResolvedCrewMemberIdentity {
  const binding = extractCrewMemberTemplateBinding(input.displayPreferences)
  if (binding) {
    const identity = crewMemberIdentityFromTemplateBinding(binding)
    return {
      ...identity,
      source: 'template_bind',
      persistent: true,
      templateId: binding.templateId,
      bindStatus: 'TEMPLATE_BOUND'
    }
  }

  const draft = extractCrewMemberDraft(input.cardTypePreferences)
  if (draft) {
    return {
      ...crewMemberIdentityFromDraft(draft),
      source: 'member_draft',
      persistent: true,
      bindStatus: CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
    }
  }

  const template = getCrewMemberTemplate(input.templateId)
  if (template) {
    return {
      ...crewMemberIdentityFromTemplate(template),
      source: 'template_registry',
      persistent: false,
      templateId: template.id
    }
  }

  const mateId =
    resolveMateId(input.fallbackMateId) ??
    DEFAULT_MATE_ID
  const label = MATE_BY_ID.get(mateId)?.label ?? mateId
  const displayName =
    typeof input.fallbackDisplayName === 'string' &&
    input.fallbackDisplayName.trim()
      ? input.fallbackDisplayName.trim().slice(0, CREW_MEMBER_IDENTITY_DISPLAY_NAME_MAX)
      : label

  return {
    mateId,
    displayName,
    source: 'catalog_default',
    persistent: false
  }
}

/**
 * Distinct accent for the three plush SHAPES (petal/jovi/star). The catalog
 * gives all three the same soft stage wash (#E8DCC8), which fails the
 * "unique color per mate" requirement in a thread. The feed surface already
 * brands them sky / emerald / amber (see lib/crew/mate-accent.ts) — mirror
 * those hex values here so the bubble accent matches the rest of Crew.
 *
 * NOTE (open item): these are the Tailwind sky-500 / emerald-500 / amber-500
 * hexes used by the feed wash. Swap for final brand tokens if design ships a
 * dedicated per-mate palette.
 */
const SHAPE_RENDER_ACCENT: Partial<Record<MateId, string>> = {
  star: '#0ea5e9', // sky-500 (feed: etoile)
  petal: '#10b981', // emerald-500 (feed: peta)
  jovi: '#f59e0b' // amber-500 (feed: jovi)
}

/** True when identity will survive a process/session restart. */
export function isCrewMemberIdentityPersistent(
  resolved: ResolvedCrewMemberIdentity
): boolean {
  return resolved.persistent
}

// ---------------------------------------------------------------------------
// Render-layer identity lookup (C0 message bubble wire-up)
//
// The message render layer must resolve a mate's visual identity (name, color
// accent, avatar) from a bare `mateId` WITHOUT coupling to the binding/mandate
// internals. These helpers consume the shared catalog only — they do not touch
// Space binding resolution or session storage.
// ---------------------------------------------------------------------------

/**
 * Visual identity surface a message bubble needs to render a mate distinctly:
 * display name, a unique color accent, and (where available) a plush avatar
 * path. Resolved from a bare `mateId` via the shared catalog only.
 */
export type CrewMateRenderIdentity = {
  mateId: MateId
  /** Display label (e.g. "Peta", "Star", "Fox"). */
  name: string
  /** Hex color accent pulled from the catalog — unique per mate family. */
  color: string
  /**
   * Plush image path (CDN convention, `/mates/{id}.jpg`). Available for
   * consumers that ship the plush assets (crew-mate app / mobile). The web
   * product app does NOT bundle these JPGs — web surfaces render the shape
   * via LosiAvatar or an accent-colored monogram instead. Treat as optional.
   */
  avatarSrc: string
}

/**
 * Resolve a bare `mateId` to its catalog Mate, or null when the id is empty,
 * unknown, or does not match a known mate. Accepts engine-shape aliases
 * (etoile→star, peta→petal) via `resolveMateId`.
 *
 * Fail-closed: callers in the render layer treat `null` as "render the
 * standard Losi assistant bubble".
 */
export function getMateById(
  mateId: string | null | undefined
): Mate | null {
  const resolved = resolveMateId(mateId ?? null)
  if (!resolved) return null
  return MATE_BY_ID.get(resolved) ?? null
}

/**
 * Resolve a bare `mateId` to the visual identity the message bubble renders.
 * Returns null for empty / unknown ids so the bubble falls back to Losi.
 */
export function getMateRenderIdentity(
  mateId: string | null | undefined
): CrewMateRenderIdentity | null {
  const mate = getMateById(mateId)
  if (!mate) return null
  return {
    mateId: mate.id,
    name: mate.label,
    color: SHAPE_RENDER_ACCENT[mate.id] ?? mate.accent,
    avatarSrc: `/mates/${mate.id}.jpg`
  }
}
